import { NewPassword } from '../../components/NewPassword';
import { useEffect, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Callout, Flex, Link, Text, TextField } from '@dodilio/ui-design-lib';
import { HeroShell } from '../../components/HeroShell';
import { PageMeta } from '../../components/PageMeta';
import { ResendCode } from '../../components/ResendCode';
import { ServiceRegister } from '../../api';
import { signInToFinish } from '../../auth';
import { errorText } from '../../errorText';
import { readApiError } from '../../apiError';
import { tabStore } from '../../tabStore';
import { signupLinkAtStart } from '../../signupLink';
import { withNewPassword } from '../../password';
import { codeRule } from '../VerifyAccount/schema';

/*
 * Forgot password, on iam's code-based reset so it looks like signup:
 * an email, then the emailed code and a new password. The first step
 * never says whether the address has an account. The email is kept for the
 * tab so a refresh resumes at the code; the password never is.
 */

const KEY = 'dodil_reset_email';

const emailSchema = z.object({ email: z.string().trim().email("That doesn't look like a valid email. Please check the format.") });
const resetSchema = withNewPassword({ verificationCode: codeRule });

/**
 * The address got no code, but saying so would tell anyone which addresses
 * have accounts. An older iam answers NOT_FOUND (or INTERNAL "not found");
 * the same neutral answer stands for it.
 */
const nothingToSay = (e: unknown) => {
    const { code, raw } = readApiError(e);
    return code === 5 || (code === 13 && /not found/i.test(raw));
};

const Pane = () => (
    <>
        <h1 style={{ fontSize: 'clamp(22px, 2.6vw, 28px)', fontWeight: 500, lineHeight: 1.06, letterSpacing: '-0.03em', margin: '0 0 18px', color: 'var(--gray-12)' }}>
            Reset your{' '}
            <span style={{ color: 'var(--gray-11)', fontWeight: 300, fontSize: '0.78em' }}>password.</span>
        </h1>
        <Text as="p" size="2" className="heroPaneDesc" style={{ color: 'var(--gray-11)', margin: '0 0 22px', lineHeight: 1.5 }}>
            We email you a 6-digit code. It expires in 5 minutes.
        </Text>
    </>
);

const Alert = ({ tone, children }: { tone: 'error' | 'ok' | 'info'; children: ReactNode }) => (
    <Callout.Root mb="4" size="1" variant="soft" color={tone === 'error' ? 'tomato' : tone === 'ok' ? 'green' : 'gray'} role={tone === 'error' ? 'alert' : 'status'}>
        <Callout.Text>{children}</Callout.Text>
    </Callout.Root>
);

const AskEmail = ({ onSent }: { onSent: (email: string) => void }) => {
    const [error, setError] = useState('');
    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<z.infer<typeof emailSchema>>({
        resolver: zodResolver(emailSchema),
        defaultValues: { email: signupLinkAtStart().email ?? '' },
    });
    const send = async ({ email }: { email: string }) => {
        setError('');
        try {
            await ServiceRegister.forgetPasswordInit(email);
        } catch (e) {
            if (!nothingToSay(e)) { setError(errorText(e, 'reset')); return; }
        }
        onSent(email);
    };
    return (
        <form onSubmit={handleSubmit(send)}>
            {error && <Alert tone="error">{error}</Alert>}
            <Flex direction="column" gap="3">
                <TextField.Root {...register('email')} type="email" size="2" variant="surface" autoFocus aria-label="Email" autoComplete="email" color={errors.email ? 'tomato' : 'gray'}>
                    <TextField.Slot className="fieldLabel"><Text size="1">Email</Text></TextField.Slot>
                </TextField.Root>
                {errors.email && <Text size="1" className="field-error" role="alert">{errors.email.message}</Text>}
                <Button radius="full" type="submit" size="3" variant="solid" mt="2" disabled={isSubmitting} loading={isSubmitting}>Send the code</Button>
            </Flex>
        </form>
    );
};

const ChooseNew = ({ email, justSent, onOtherEmail, onDone }: { email: string; justSent: boolean; onOtherEmail: () => void; onDone: () => void }) => {
    const [error, setError] = useState('');
    const [notice, setNotice] = useState(justSent ? `If there's an account for ${email}, we've sent a 6-digit code.` : `Enter the code we sent to ${email}.`);
    const { register, handleSubmit, clearErrors, watch, formState: { errors, isSubmitting } } = useForm<z.infer<typeof resetSchema>>({
        resolver: zodResolver(resetSchema),
        mode: 'onSubmit',
    });
    const resend = async (): Promise<boolean> => {
        setError('');
        try {
            await ServiceRegister.forgetPasswordInit(email);
        } catch (e) {
            if (!nothingToSay(e)) { setError(errorText(e, 'reset')); return false; }
        }
        clearErrors();
        setNotice(`If there's an account for ${email}, we've sent a new code.`);
        return true;
    };
    const reset = async (d: z.infer<typeof resetSchema>) => {
        setError('');
        try {
            await ServiceRegister.forgetPassword({ email, verificationCode: d.verificationCode, newPassword: d.password });
            onDone();
        } catch (e) {
            setError(errorText(e, 'reset'));
        }
    };
    return (
        <form onSubmit={handleSubmit(reset)}>
            {error ? <Alert tone="error">{error}</Alert> : notice && <Alert tone="info">{notice}</Alert>}
            <Flex direction="column" gap="3">
                <TextField.Root {...register('verificationCode')} type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus size="2" variant="surface" placeholder="XXXXXX" aria-label="Verification code" color={errors.verificationCode ? 'tomato' : 'gray'}>
                    <TextField.Slot className="fieldLabel"><Text size="1">Code</Text></TextField.Slot>
                </TextField.Root>
                {errors.verificationCode && <Text size="1" className="field-error" role="alert">{errors.verificationCode.message}</Text>}
                <NewPassword field={register('password')} value={watch('password') ?? ''} error={errors.password?.message} label="New password" />
                <Flex mt="4" align="center" justify="between" gap="3">
                    <Flex gap="5" align="center">
                        <Link href="/reset" underline="always" size="2" onClick={(e: React.MouseEvent) => { e.preventDefault(); onOtherEmail(); }}>Use another email</Link>
                        <ResendCode onResend={resend} coolingFrom={justSent ? 60 : 0} />
                    </Flex>
                    <Button radius="full" type="submit" size="2" variant="classic" disabled={isSubmitting} loading={isSubmitting}>Change password</Button>
                </Flex>
            </Flex>
        </form>
    );
};

export const ResetPassword = () => {
    // A refresh resumes at the code for the same address.
    const [email, setEmail] = useState<string | null>(() => tabStore.get(KEY));
    const [justSent, setJustSent] = useState(false);
    const [done, setDone] = useState<string | null>(null);

    // Changed: on to Keycloak's sign-in with the address filled in, then where they were going.
    useEffect(() => {
        if (!done) return;
        const t = window.setTimeout(() => { void signInToFinish(done); }, 1500);
        return () => window.clearTimeout(t);
    }, [done]);

    let body: ReactNode;
    if (done) {
        body = (
            <Flex direction="column" gap="3">
                <Alert tone="ok">Your password is changed. Taking you to sign in…</Alert>
                <Button radius="full" size="2" onClick={() => void signInToFinish(done)}>Sign in</Button>
            </Flex>
        );
    } else if (!email) {
        body = <AskEmail onSent={(e) => { tabStore.set(KEY, e); setJustSent(true); setEmail(e); }} />;
    } else {
        body = (
            <ChooseNew
                email={email}
                justSent={justSent}
                onOtherEmail={() => { tabStore.remove(KEY); setJustSent(false); setEmail(null); }}
                onDone={() => { tabStore.remove(KEY); setDone(email); }}
            />
        );
    }

    return (
        <>
            <PageMeta title="Reset your password · Dodil Cloud" description="Reset your Dodil password with a code we email you." />
            <HeroShell title="Reset your password" leftPane={<Pane />}>{body}</HeroShell>
        </>
    );
};
